import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import { getApiUrl } from '../config/api-config';

/**
 * The four moments a visitor reaches for something they do not yet have.
 *
 * Copy is fixed by ANONYMOUS_EVALUATION_MODE.md section 3.5.3 and should be
 * changed there first. The pattern in all four: **the artifact already exists
 * and is being held**, never "upgrade to unlock a feature". The first is about
 * something of theirs; the second is about our price list.
 */
export type GateKind = 'flattened' | 'fillable' | 'project' | 'sessions';

const GATE_COPY: Record<GateKind, string> = {
    flattened: 'Your completed PDF is ready. Create an account to download it.',
    fillable: 'Your fillable PDF is ready. Create an account to download it.',
    project: 'Your Spring Boot project is ready to generate. Create an account to download it.',
    sessions: 'Save this session and pick up where you left off. Available with an account.',
};

/** Which capability each gate consults. One place, so the UI cannot invent its own rule. */
const GATE_CAPABILITY: Record<GateKind, keyof ReturnType<typeof useCapabilityFlags>> = {
    flattened: 'canDownloadFlattenedPdf',
    fillable: 'canDownloadFillablePdf',
    project: 'canGenerateProject',
    sessions: 'canUseSessionLibrary',
};

function useCapabilityFlags() {
    const { capabilities } = useCapabilities();
    return {
        canDownloadFlattenedPdf: capabilities?.canDownloadFlattenedPdf ?? false,
        canDownloadFillablePdf: capabilities?.canDownloadFillablePdf ?? false,
        canGenerateProject: capabilities?.canGenerateProject ?? false,
        canUseSessionLibrary: capabilities?.canUseSessionLibrary ?? false,
    };
}

interface ArtifactGateValue {
    /** Whether this action is permitted for the current session. */
    allows: (gate: GateKind) => boolean;
    /**
     * Run the action if permitted, otherwise open the prompt for that gate.
     *
     * Deliberately NOT "disable the button". Section 3.5.4: a disabled control
     * gives no feedback on click and is skipped by screen readers. These
     * controls are live, and clicking one opens the sign-up path, which is the
     * intended journey rather than a dead end.
     */
    guard: (gate: GateKind, action: () => void) => void;
    /**
     * Show a capacity refusal the SERVER has already made.
     *
     * `guard` is pre-emptive - it asks the capabilities before acting. This is
     * reactive: the request went out, the server said no, and it supplied the
     * sentence. Section 3.3.2 governs the wording: the sentence stays factual
     * and the ACTIONS are concrete. Mixing a capacity limit with an upsell
     * teaches people the technical limits are negotiable, which undermines
     * every real one.
     */
    showLimit: (message: string) => void;
    /**
     * Open a gate prompt directly, for a refusal the SERVER made.
     *
     * `guard` consults the client's capability snapshot, which can be stale -
     * and if it says "allowed" while the server says otherwise, guard would run
     * the action and show nothing. A refusal that has already happened must not
     * depend on the client agreeing that it should have.
     */
    showGate: (gate: GateKind) => void;
}

const ArtifactGateContext = createContext<ArtifactGateValue | null>(null);

interface ProviderProps {
    children: ReactNode;
    onStartTrial: () => void;
    onOpenSignIn: () => void;
}

export const ArtifactGateProvider: React.FC<ProviderProps> = ({ children, onStartTrial, onOpenSignIn }) => {
    const flags = useCapabilityFlags();
    const [openGate, setOpenGate] = useState<GateKind | null>(null);
    const [limitMessage, setLimitMessage] = useState<string | null>(null);

    // The "have someone reach out" path. Deliberately a third, QUIETER option:
    // the two buttons above are the decision, and a third of equal weight would
    // dilute the moment this prompt exists to create. Someone who clicks a text
    // link beneath them has selected themselves, which is what makes the lead
    // worth Stacey's time.
    const [contactOpen, setContactOpen] = useState(false);
    const [contactEmail, setContactEmail] = useState('');
    const [contactNote, setContactNote] = useState('');
    const [contactState, setContactState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

    const sendContactRequest = async () => {
        setContactState('sending');
        try {
            const response = await fetch(getApiUrl('/api/pdf/contact-request'), {
                method: 'POST',
                // The guest cookie is what lets the server attach a link to the
                // work this person has done. Without it the lead is just an
                // email address.
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: contactEmail, comments: contactNote }),
            });
            const data = await response.json();
            setContactState(data?.success ? 'sent' : 'failed');
        } catch {
            setContactState('failed');
        }
    };

    const allows = useCallback(
        (gate: GateKind) => flags[GATE_CAPABILITY[gate]],
        [flags]
    );

    const guard = useCallback(
        (gate: GateKind, action: () => void) => {
            if (flags[GATE_CAPABILITY[gate]]) {
                action();
            } else {
                setOpenGate(gate);
            }
        },
        [flags]
    );

    const showLimit = useCallback((message: string) => setLimitMessage(message), []);
    const showGate = useCallback((gate: GateKind) => setOpenGate(gate), []);

    // One prompt, two reasons for it. A gate is "we are holding something of
    // yours"; a limit is "this session is a size and it is full". The buttons
    // are the same because the way forward is the same.
    const promptOpen = openGate !== null || limitMessage !== null;
    const closePrompt = () => {
        setOpenGate(null);
        setLimitMessage(null);
        setContactOpen(false);
        setContactState('idle');
    };

    return (
        <ArtifactGateContext.Provider value={{ allows, guard, showLimit, showGate }}>
            {children}
            {promptOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="artifact-gate-message"
                    onClick={closePrompt}
                >
                    <div
                        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p id="artifact-gate-message" className="text-base text-neutral-900 mb-1">
                            {limitMessage ?? GATE_COPY[openGate!]}
                        </p>
                        <p className="text-sm text-neutral-500 mb-5">
                            {limitMessage
                                ? 'Sign in or start a trial to keep going — your PDF and the fields you’ve built come with you.'
                                : 'Your PDF and the fields you’ve built come with you.'}
                        </p>

                        <div className="flex gap-3">
                            <button
                                onClick={() => { closePrompt(); onStartTrial(); }}
                                className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-md"
                            >
                                Start trial
                            </button>
                            <button
                                onClick={() => { closePrompt(); onOpenSignIn(); }}
                                className="flex-1 bg-neutral-100 text-neutral-700 text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-neutral-200 transition-all duration-200"
                            >
                                Sign in
                            </button>
                        </div>

                        {/* Quieter by design - see the state declaration above. */}
                        {!contactOpen && contactState === 'idle' && (
                            <button
                                onClick={() => setContactOpen(true)}
                                className="mt-3 w-full text-xs text-neutral-500 hover:text-neutral-700 underline decoration-neutral-300 transition-colors"
                            >
                                Questions? Have someone reach out
                            </button>
                        )}

                        {contactOpen && contactState !== 'sent' && (
                            <div className="mt-3 pt-3 border-t border-neutral-100 space-y-2">
                                <input
                                    type="email"
                                    value={contactEmail}
                                    onChange={(e) => setContactEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    aria-label="Your email address"
                                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                <textarea
                                    value={contactNote}
                                    onChange={(e) => setContactNote(e.target.value)}
                                    rows={2}
                                    placeholder="What are you trying to build? (optional)"
                                    aria-label="What are you trying to build"
                                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                                />
                                <button
                                    onClick={sendContactRequest}
                                    disabled={contactState === 'sending' || !contactEmail.includes('@')}
                                    className="w-full bg-neutral-800 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-neutral-900 disabled:bg-neutral-300 transition-colors"
                                >
                                    {contactState === 'sending' ? 'Sending…' : 'Ask us to get in touch'}
                                </button>
                                {contactState === 'failed' && (
                                    <p className="text-xs text-red-600">
                                        We could not record that just now — please email us directly.
                                    </p>
                                )}
                                <p className="text-xs text-neutral-400">
                                    We’ll only use this to answer your question.
                                </p>
                            </div>
                        )}

                        {contactState === 'sent' && (
                            <div className="mt-3 pt-3 border-t border-neutral-100">
                                <p className="text-sm text-neutral-700">
                                    Thanks — we’ll be in touch shortly.
                                </p>
                            </div>
                        )}

                        <button
                            onClick={closePrompt}
                            className="mt-3 w-full text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
                        >
                            Keep looking around
                        </button>
                    </div>
                </div>
            )}
        </ArtifactGateContext.Provider>
    );
};

export const useArtifactGate = (): ArtifactGateValue => {
    const ctx = useContext(ArtifactGateContext);
    if (!ctx) {
        throw new Error('useArtifactGate must be used within an ArtifactGateProvider');
    }
    return ctx;
};

/**
 * The lock affordance for a gated control.
 *
 * Section 3.5.4 wants gated actions **visible and clearly marked** — visible so
 * the visitor learns the product does the thing, marked so the click is
 * informed rather than a failure. Rendered next to the label, not instead of it.
 */
export const GateLock: React.FC<{ show: boolean }> = ({ show }) =>
    show ? (
        <span className="ml-2 text-xs opacity-80" aria-label="requires an account" title="Requires an account">
            🔒
        </span>
    ) : null;
